import { useEffect, useRef, useState, useCallback } from "react";
import { notification } from "antd";
import { useAuthStore } from "../../store/auth/authStore";
import useIsMobile from "../../hooks/useIsMobile";
import TripsLogo from "../../assets/images/trips-logo.svg";
import {
  ModalContainer,
  StyledModal,
  StyledInput,
  LogoContainer,
  Title,
  SocialButton,
  DividerContainer,
  FormContainer,
  InputLabel,
  DisclaimerText,
  SubmitButton,
  TermsText,
  OTPContainer,
  ResendText,
  Spinner,
  ResendTextButton,
  LoadingOtpContainer,
  OtpContainer,
  LastLoginText,
  TermsLink,
  OtpError,
  OtpInput,
  DrawerHandle,
  AuthDrawerStyle,
  ErrorMessage,
} from "./styles";
import { ReactComponent as GoogleIcon } from "../../assets/images/google-icon.svg";
import {
  LOGIN_URL,
  VERIFY_OTP_URL,
  RESEND_OTP_URL,
  GOOGLE_LOGIN,
  GOOGLE_CALLBACK_URL,
  USER,
} from "../../utils/constants";
import { encryptedPostAPI, getAPICustom } from "../../utils/api";
import { EncryptionHelper } from "../../utils/encryption";
import { getHostUrl, isEmbedded } from "../../utils/navigation";
import OTPInput from "react-otp-input";
const RESEND_TIMEOUT = 60;

export default function AuthModals() {
  const { isMobile } = useIsMobile();
  const {
    showAuthModal,
    showOTPModal,
    setAuthModal,
    setOTPModal,
    login,
    setB2cAuth,
    setLastLoginMethod,
    lastLoginMethod,
  } = useAuthStore();
  const [localEmail, setLocalEmail] = useState("");
  const auth = localStorage.getItem("trips_auth");
  const obj: UserAuth = auth ? JSON.parse(auth) : null;
  const merchantHash = obj?.Hash;
  const merchantCode = obj?.MerchantCode;
  const merchantToken = obj?.Token;
  const merchantName = obj?.MerchantName;
  const [requesting, setRequesting] = useState(false);
  const [signinMethod, setSignInMethod] = useState("");
  const [timeLeft, setTimeLeft] = useState(RESEND_TIMEOUT);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const fetchUserDetail = useCallback(async (access_token: string, refreshToken?: string | null, expiresIn?: number | null) => {
    notification.info({ message: "Signing you in...",  duration: 0 });
    try {
      const response = await getAPICustom(USER, null, {
        Authorization: `Bearer ${merchantToken}`,
        MerchantCode: merchantCode,
        AppName: merchantName,
        MerchantHash: merchantHash,
        B2CAuthorization: `Bearer ${access_token}`,
      });
      if (response.status === 200) {
        const decryptedData = await EncryptionHelper.decrypt(
          response?.data,
          merchantHash,
          merchantCode,
        );
        const parsedData = JSON.parse(decryptedData);
        const { WalletBalance, Email, FullName, UserId, ProfileImage } = parsedData;
        setB2cAuth(access_token, { WalletBalance, Email, FullName, UserId, ProfileImage }, refreshToken ?? undefined, expiresIn ?? undefined);
        setLastLoginMethod("google");
        login();
        // notify parent website of successful login
        const userObj = { WalletBalance, Email, FullName, UserId, ProfileImage };
        const loginPayload = await EncryptionHelper.encrypt(
          JSON.stringify({ AccessToken: access_token, User: userObj, RefreshToken: refreshToken ?? null, ExpiresIn: expiresIn ?? undefined }),
          merchantHash,
          merchantCode,
        );
        window.parent.postMessage({ type: 'TRIPS_LOGIN', data: loginPayload }, '*');
        
        notification.success({ message: "Signed in successfully!", key: "google-auth" });
      } else {
        const decryptedData = await EncryptionHelper.decrypt(
          response?.data,
          merchantHash,
          merchantCode,
        );
        const parsedDecryptedData = JSON.parse(decryptedData);
        notification.error({ message: response?.data?.Message || parsedDecryptedData?.Message || "Failed to fetch user's detail for sign in" });
      }
    } catch (error: any) {
      let errMsg = error?.message || "Failed to sign in";
      try {
        const d = await EncryptionHelper.decrypt(error?.data, merchantHash, merchantCode);
        errMsg = JSON.parse(d)?.Message || errMsg;
        notification.error({ message: errMsg });
      } catch (_) {
        notification.error({ message: errMsg });
      }
    }
  }, [merchantToken, merchantCode, merchantName, merchantHash]);

  // Receive token from Google auth popup (iframe mode) — close modal immediately, process in background
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "GOOGLE_AUTH_TOKEN") {
        setAuthModal(false);
        setOTPModal(false);
        fetchUserDetail(event.data.token, event.data.refreshToken, event.data.expiresIn);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [fetchUserDetail]);

  useEffect(() => {
    const handleWebsiteMessage = async (event: MessageEvent) => {
      if (event.data?.type !== "WEBSITE_TOKEN_REFRESH" || !event.data?.data) return;
      try {
        const decrypted = await EncryptionHelper.decrypt(event.data.data, merchantHash, merchantCode);
        const parsed = typeof decrypted === "string" ? JSON.parse(decrypted) : decrypted;
        const { accessToken, refreshToken } = parsed;
        if (!accessToken) return;
        const currentUser = useAuthStore.getState().b2CUser || {};
        setB2cAuth(accessToken, currentUser, refreshToken ?? useAuthStore.getState().refreshToken);
      } catch (err) {
        console.error("Failed to apply WEBSITE_TOKEN_REFRESH:", err);
      }
    };
    window.addEventListener("message", handleWebsiteMessage);
    return () => window.removeEventListener("message", handleWebsiteMessage);
  }, [merchantHash, merchantCode]);


  const startTimer = () => {
    setTimeLeft(RESEND_TIMEOUT);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    startTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleEmailSubmit = async () => {
    setSignInMethod("email");
    setRequesting(true);
    setErrorMessage("");
    const loginURL = LOGIN_URL;
    const stringPayload = JSON.stringify({ Email: localEmail });
    const encryptedPayload = await EncryptionHelper.encrypt(
      stringPayload,
      merchantHash,
      merchantCode,
    );
    try {
      let loginResponse = await encryptedPostAPI(loginURL, encryptedPayload);
      if (loginResponse.status === 200) {
        setAuthModal(false);
        startTimer();
        setOTPModal(true);
      } else {
        if (typeof loginResponse?.data === "string"){
          const decryptedData = await EncryptionHelper.decrypt(
            loginResponse?.data,
            merchantHash,
            merchantCode,
          )
          const parsedDecryptedData = JSON.parse(decryptedData);
          const error = parsedDecryptedData?.data?.Message || parsedDecryptedData?.Message || "Failed to send verification code";
          setErrorMessage(error)
          notification.error({
            message: error,
          });
        }
        else{
          const error = loginResponse?.data?.Message || loginResponse?.data?.data?.Message || loginResponse?.data?.Message || "Failed to send verification code";
          setErrorMessage(error)
          notification.error({
            message: error,
          });
        }
      }
    } catch (error: any) {
      let errMsg = error?.data?.message || error?.message || error?.data?.Message || "Failed to send verification code";
      try {
        const d = await EncryptionHelper.decrypt(error?.data, merchantHash, merchantCode);
        errMsg = JSON.parse(d)?.Message || errMsg;
        setErrorMessage(errMsg)
        notification.error({ message: errMsg });
      } catch (_) {
        setErrorMessage(errMsg)
        notification.error({ message: errMsg });
      }
    } finally {
      setRequesting(false);
    }
  };

  const handleGoogleSignin = async () => {
    setRequesting(true);
    setSignInMethod("google");

    const googleLoginURL = GOOGLE_LOGIN;
    const Payload = {
      ReturnUrl: `${window.location.origin}${GOOGLE_CALLBACK_URL}`,
    };

    try {
      let googleLoginResponse = await getAPICustom(googleLoginURL, Payload, {
        Authorization: `Bearer ${merchantToken || ""}`,
        MerchantCode: merchantCode,
        AppName: merchantName,
        MerchantHash: merchantHash,
      });

      if (googleLoginResponse.status === 200) {
        setLastLoginMethod("google");
        const googleAuthUrl = googleLoginResponse?.data?.googleAuthUrl;
        const isInIframe = window.self !== window.top;

        if (isInIframe) {
          window.open(googleAuthUrl, "_blank");
        } else {
          window.location.href = googleAuthUrl;
        }
      } else {
        const decryptedData = await EncryptionHelper.decrypt(
            googleLoginResponse?.data,
            merchantHash,
            merchantCode,
          );
        const parsedDecryptedData = JSON.parse(decryptedData);
        notification.error({
          message: googleLoginResponse?.data?.Message ||
            parsedDecryptedData?.Message ||
            "Failed to initiate Google sign in process",
          
        });
      }
    } catch (error: any) {
      let errMsg = error?.message || "Failed to initiate Google sign in process";
      try {
        const d = await EncryptionHelper.decrypt(error?.data, merchantHash, merchantCode);
        errMsg = JSON.parse(d)?.Message || errMsg;
        notification.error({ message: errMsg });
      } catch (_) {
        notification.error({ message: errMsg });
      }
    } finally {
      setRequesting(false);
    }
  };

  const navigateToParent = (path: string) => {
    const base = getHostUrl() || '';
    const url = `${base}/${path}`;
    if (isEmbedded()) {
      try {
        (window.top as Window).location.href = url;
        return;
      } catch {}
    }
    window.location.assign(url);
  };

  const resendOtp = async () => {
    setResendingOtp(true);
    const resendUrl = RESEND_OTP_URL;
    const stringPayload = JSON.stringify({ Email: localEmail });
    const encryptedPayload = await EncryptionHelper.encrypt(
      stringPayload,
      merchantHash,
      merchantCode,
    );
    try {
      let resendResponse = await encryptedPostAPI(resendUrl, encryptedPayload);

      if (resendResponse.status === 200) {
        startTimer();
        notification.success({
          message: "Code resent successfully",
        })
      } else {
        if (typeof resendResponse?.data === "string"){
          const decryptedData = await EncryptionHelper.decrypt(
            resendResponse?.data,
            merchantHash,
            merchantCode,
          )
          const parsedDecryptedData = JSON.parse(decryptedData);
          const error = parsedDecryptedData?.data?.Message || parsedDecryptedData?.Message || "Failed to send verification code";
          setErrorMessage(error)
          notification.error({
            message: error,
          });
        }
        else{
          const error = resendResponse?.data?.Message || resendResponse?.data?.data?.Message || resendResponse?.data?.Message || "Failed to send verification code";
          setErrorMessage(error)
          notification.error({
            message: error,
          });
        }
      }
    } catch (error: any) {
      let errMsg = error?.message || "Failed to send verification code";
      try {
        const d = await EncryptionHelper.decrypt(error?.data, merchantHash, merchantCode);
        errMsg = JSON.parse(d)?.Message || errMsg;
        setErrorMessage(errMsg)
        notification.error({ message: errMsg });
      } catch (_) {
        setErrorMessage(errMsg)
        notification.error({ message: errMsg });
      }
    } finally {
      setResendingOtp(false);
    }
  };

  const handleOtpChange = async (value: string) => {
    setOtp(value);
    if (value.length !== 6) {
      return;
    } else {
      setRequesting(true);
      const verifyOtpURL = VERIFY_OTP_URL;
      const extractedOtp = value;
      const stringPayload = JSON.stringify({
        Email: localEmail,
        Otp: extractedOtp,
      });
      const encryptedPayload = await EncryptionHelper.encrypt(
        stringPayload,
        merchantHash,
        merchantCode,
      );
      try {
        let loginResponse = await encryptedPostAPI(
          verifyOtpURL,
          encryptedPayload,
        );
        if (loginResponse.status === 200) {
          setOTPModal(false);
          setLastLoginMethod("email");
          const decryptedData = await EncryptionHelper.decrypt(
            loginResponse?.data,
            merchantHash,
            merchantCode,
          );
          const parsedDecryptedData = JSON.parse(decryptedData);

          const { AccessToken, User, RefreshToken, ExpiresIn } = parsedDecryptedData;
          // Setting auth details
          setB2cAuth(AccessToken, User, RefreshToken, ExpiresIn);
          setLocalEmail("");
          setOtp("");
          login();
          // notify parent website of successful login
          const loginPayload = await EncryptionHelper.encrypt(
            JSON.stringify({ AccessToken, User, RefreshToken, ExpiresIn}),
            merchantHash,
            merchantCode,
          );
          window.parent.postMessage({ type: 'TRIPS_LOGIN', data: loginPayload }, '*');

        } else {
           if (typeof loginResponse?.data === "string"){
          const decryptedData = await EncryptionHelper.decrypt(
            loginResponse?.data,
            merchantHash,
            merchantCode,
          )
          const parsedDecryptedData = JSON.parse(decryptedData);
          const error = parsedDecryptedData?.data?.Message || parsedDecryptedData?.Message || "Failed to verify code";
          setErrorMessage(error)
          notification.error({
            message: error,
          });
        }
        else{
          const error = loginResponse?.data?.Message || loginResponse?.data?.data?.Message || loginResponse?.data?.Message || "Failed to verify code";
          setErrorMessage(error)
          notification.error({
            message: error,
          });
        }
        }
      } catch (error: any) {
        let errMsg = error?.message || "Failed to verify code";
        try {
          const d = await EncryptionHelper.decrypt(error?.data, merchantHash, merchantCode);
          errMsg = JSON.parse(d)?.Message || errMsg;
          setErrorMessage(errMsg)
          notification.error({ message: errMsg });
        } catch (_) {
          setErrorMessage(errMsg)
          notification.error({ message: errMsg });
        }
      } finally {
        setRequesting(false);
      }
    }
  };

  const renderAuthContent = () => (
    <ModalContainer>
      <LogoContainer>
        <img src={TripsLogo} alt="TRIPS" height="30" />
      </LogoContainer>
      <Title>Log in or sign up</Title>
      <SocialButton
        style={{ position: "relative" }}
        onClick={handleGoogleSignin}
        disabled={requesting && signinMethod === "google"}>
        {lastLoginMethod === "google" && (
          <LastLoginText>Last used</LastLoginText>
        )}
        {requesting && signinMethod === "google" ? (
          <Spinner variant="white" />
        ) : (
          <>
            <GoogleIcon /> Continue with google
          </>
        )}
      </SocialButton>

      {/* <SocialButton>
        <AppleFilled /> Continue with apple
      </SocialButton> */}

      <DividerContainer>
        <span>OR</span>
      </DividerContainer>

      <FormContainer>
        <InputLabel>Email address</InputLabel>
        <div>
          <StyledInput
          size="large"
          value={localEmail}
          onChange={(e) => { setLocalEmail(e.target.value); setErrorMessage(""); }}
        />
        <ErrorMessage>
          {errorMessage || ""}
        </ErrorMessage>
        </div>
        <DisclaimerText>
          We will send a temporary code to verify this email
        </DisclaimerText>

        <SubmitButton onClick={handleEmailSubmit} disabled={requesting}>
          {requesting && signinMethod === "email" ? (
            <Spinner variant="white" />
          ) : (
            "Continue"
          )}
        </SubmitButton>

        <TermsText>
          By continuing you accept the{" "}
          <TermsLink href="#" onClick={(e) => { e.preventDefault(); navigateToParent('terms-of-use'); }}>Terms of Use</TermsLink> and{" "}
          <TermsLink href="#" onClick={(e) => { e.preventDefault(); navigateToParent('privacy-policy'); }}>Privacy Policy</TermsLink>
        </TermsText>
      </FormContainer>
    </ModalContainer>
  );

  const renderOTPContent = () => (
    <ModalContainer>
      <LogoContainer>
        <img src={TripsLogo} alt="TRIPS" height="32" />
      </LogoContainer>
      <Title style={{ marginBottom: "0" }}>Verify your email</Title>
      <div
        style={{ textAlign: "center", marginBottom: "32px", color: "#4B5563" }}>
        We sent a temporary code to <strong>{localEmail}</strong>
      </div>
      <OtpContainer>
        {requesting && (
          <LoadingOtpContainer>
            <Spinner size={40} />
          </LoadingOtpContainer>
        )}
        <OTPContainer>
          <OTPInput
            value={otp}
            onChange={handleOtpChange}
            numInputs={6}
            inputType="tel"
            renderInput={(props: any) => <OtpInput {...props} />}
            inputStyle={{
              width: isMobile ? "40px" : "50px",
              height: isMobile ? "48px" : "58px",
              margin: isMobile ? "0 4px" : "0 5px",
              fontSize: "18px",
              fontWeight: "500",
              borderRadius: isMobile ? "10px" : "20px",
              border: "1px solid #e4e4e7",
              outline: "none",
              textAlign: "center",
            }}
          />
          <ErrorMessage className="centered">
            {errorMessage || ""}
          </ErrorMessage>
        </OTPContainer>

        {timeLeft > 0 ? (
          <ResendText>Resend in {timeLeft} secs</ResendText>
        ) : (
          <ResendTextButton onClick={resendOtp} disabled={resendingOtp}>
            {resendingOtp ? "Resending..." : "Resend"}
          </ResendTextButton>
        )}
      </OtpContainer>
    </ModalContainer>
  );

  if (isMobile) {
    return (
      <>
        <AuthDrawerStyle
          placement="bottom"
          open={showAuthModal}
          onClose={() => setAuthModal(false)}
          height="auto"
          closable={true}
          title={<DrawerHandle />}
          headerStyle={{ borderBottom: "none", padding: "12px 24px" }}
          className="auth-bottom-drawer"
          bodyStyle={{ padding: "0px" }}>
          {renderAuthContent()}
        </AuthDrawerStyle>

        <AuthDrawerStyle
          placement="bottom"
          open={showOTPModal}
          onClose={() => setOTPModal(false)}
          height="auto"
          closable={true}
          title={<DrawerHandle />}
          headerStyle={{ borderBottom: "none", padding: "12px 0" }}
          className="auth-bottom-drawer"
          bodyStyle={{ padding: "16px" }}>
          {renderOTPContent()}
        </AuthDrawerStyle>
      </>
    );
  }

  return (
    <>
      <StyledModal
        open={showAuthModal}
        onCancel={() => {
          setAuthModal(false);
          setOtp("");
          setLocalEmail("");
        }}
        footer={null}
        width={520}
        centered
        closable={true}
        bodyStyle={{ padding: "0" }}>
        {renderAuthContent()}
      </StyledModal>

      <StyledModal
        open={showOTPModal}
        onCancel={() => {
          setOTPModal(false);
          setOtp("");
          setLocalEmail("");
        }}
        footer={null}
        width={480}
        centered
        closable={true}
        bodyStyle={{ padding: "32px 24px" }}>
        {renderOTPContent()}
      </StyledModal>
    </>
  );
}
